import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePageTitle } from "@/hooks/use-page-title";
import { useTeam } from "@/hooks/use-teams";
import { useCanManageTeams } from "@/hooks/use-can-manage-teams";
import { RenameTeamDialog } from "@/components/teams/RenameTeamDialog";
import { DeleteTeamDialog } from "@/components/teams/DeleteTeamDialog";
import { AddTeamMemberDialog } from "@/components/teams/AddTeamMemberDialog";
import { RemoveTeamMemberDialog } from "@/components/teams/RemoveTeamMemberDialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Loader2, Pencil, Plus, Trash2, UserMinus } from "lucide-react";

export function TeamDetailPage() {
  const { orgId, slug, teamId } = useParams<{
    orgId: string;
    slug: string;
    teamId: string;
  }>();
  const navigate = useNavigate();
  const canManage = useCanManageTeams();
  const { team, isPending } = useTeam(teamId);
  usePageTitle(team?.name ?? "Team");

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; name: string } | null>(null);

  const excludeUserIds = useMemo(
    () => team?.members.map((m) => m.userId) ?? [],
    [team?.members]
  );

  const backHref = `/${orgId}/${slug}/settings/teams`;

  if (isPending) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="space-y-4">
        <Link to={backHref} className="inline-flex items-center text-sm text-muted-foreground hover:underline">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to teams
        </Link>
        <p className="text-sm text-muted-foreground">Team not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={backHref}
        className="inline-flex items-center text-sm text-muted-foreground hover:underline"
      >
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to teams
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{team.name}</h1>
          <p className="text-muted-foreground">
            {team.members.length} {team.members.length === 1 ? "member" : "members"}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setRenameOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" /> Rename
            </Button>
            <Button variant="outline" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Members</h2>
          {canManage && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add member
            </Button>
          )}
        </div>

        {team.members.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No members in this team yet.</p>
        ) : (
          <div className="space-y-2">
            {team.members.map((m) => {
              const label = m.user.name ?? m.user.email;
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {label?.[0]?.toUpperCase() ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{m.user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-secondary px-2 py-1 text-xs font-medium">
                      {m.role}
                    </span>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Remove from team"
                        onClick={() => setRemoveTarget({ userId: m.userId, name: label })}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <RenameTeamDialog
        team={{ id: team.id, name: team.name }}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />
      <DeleteTeamDialog
        team={{ id: team.id, name: team.name }}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={() => navigate(backHref)}
      />
      <AddTeamMemberDialog
        teamId={team.id}
        excludeUserIds={excludeUserIds}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
      <RemoveTeamMemberDialog
        teamId={team.id}
        member={removeTarget}
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
      />
    </div>
  );
}
